import React, { useState } from 'react';
import RegisterInterest from '../../components/modals/RegisterInterest/RegisterInterest';

// Voycalc
import voycalcImage from '../../assets/voycalc.png';
import optionsImage from '../../assets/options.png';
import recentImage from '../../assets/recent-2.png';

import intakeImage from '../../assets/intake.png';
import routingImage from '../../assets/routing.png';

// Configuration
import xpdImage from '../../assets/dynamic-xpd.png';
import miscImage from '../../assets/dynamic-misc.png';
import seaConsumptionImage from '../../assets/seaconsumption-series.png';
import ecaConsumptionImage from '../../assets/eca-consumption.png';
import bunkerImage from '../../assets/bunker-in-port.png';
import canalCostImage from '../../assets/canal-cost.png';
import emissionsImage from '../../assets/emissions-factor.png';
import bunkerDefinitionsImage from '../../assets/bunker-definitions.png';

import changeImage from '../../assets/results-2.png';
import canalImage from '../../assets/canal.png';
import distanceImage from '../../assets/distance.png';
import ecaImage from '../../assets/eca.png';
import hraImage from '../../assets/hra.png';

const ProductV2: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'vessel' | 'routing' | 'emissions'>('vessel');

  const configTabs = {
    vessel: [
      { image: seaConsumptionImage, title: 'Sea Consumption', desc: 'Multiple consumption series for warranted vs expected performance.' },
      { image: ecaConsumptionImage, title: 'ECA Consumption', desc: 'Advanced matrix for ECA zones with future-dated changes.' },
      { image: bunkerImage, title: 'Bunker in Port', desc: 'Define bunker consumptions for port operations.' },
    ],
    routing: [
      { image: xpdImage, title: 'Flexible XPD', desc: 'Customize your Extra Port Days calculations.' },
      { image: canalCostImage, title: 'Canal Costs', desc: 'Cost matrix with vessel size and future pricing.' },
      { image: miscImage, title: 'MISC Settings', desc: 'Set your preferred defaults.' },
    ],
    emissions: [
      { image: emissionsImage, title: 'Emission Factors', desc: 'Configure the factors you want to use.' },
      { image: bunkerDefinitionsImage, title: 'Bunker Definitions', desc: 'Define bunker types with emission factors.' },
      { image: ecaImage, title: 'ECA Areas', desc: 'Includes route optimisation options!' },
    ],
  };

  return (
    <>
      {/* Hero Section - Large with featured image */}
      <section className="product-v2-hero">
        <div className="product-v2-hero-bg">
          <img src={voycalcImage} alt="VoyCalc" />
        </div>
        <div className="product-v2-hero-overlay" />
        <div className="product-v2-hero-content">
          <span className="product-v2-badge">The Calculator</span>
          <h1 className="product-v2-hero-title">
            Built for <span className="hero-highlight">Speed</span> & <span className="hero-highlight">Precision</span>
          </h1>
          <p className="product-v2-hero-subtitle">
            An advanced estimator with built-in efficiency features. We're constantly building new features — register your interest to see it live.
          </p>
          <RegisterInterest showSubtext={false} />
        </div>
      </section>

      {/* Quick Features Strip */}
      <section className="product-v2-strip">
        <div className="product-v2-strip-item">
          <i className="bi bi-lightning-charge-fill"></i>
          <span>Instant Calculations</span>
        </div>
        <div className="product-v2-strip-item">
          <i className="bi bi-globe"></i>
          <span>Thousands of Ports</span>
        </div>
        <div className="product-v2-strip-item">
          <i className="bi bi-shield-check"></i>
          <span>ECA, HRA & Emissions Calculations</span>
        </div>
        <div className="product-v2-strip-item">
          <i className="bi bi-gear"></i>
          <span>Fully Configurable</span>
        </div>
      </section>

      {/* Bento Grid - Main Features */}
      <section className="product-v2-section">
        <div className="product-v2-container">
          <h2 className="product-v2-section-title">Core Features</h2>
          <p className="product-v2-section-subtitle">Everything you need for voyage estimation</p>

          <div className="product-v2-bento">
            {/* Full-width featured card */}
            <div className="bento-card bento-full">
              <div className="bento-card-image">
                <img src={optionsImage} alt="VoyCalc Options" />
              </div>
              <div className="bento-card-content">
                <h3>Compare Options Instantly</h3>
                <p>Add multiple vessels from your own vessel list, or simply copy the estimate and explore impact by playing with the intake.</p>
              </div>
            </div>

            {/* Medium cards */}
            <div className="bento-card bento-medium">
              <div className="bento-card-image">
                <img src={recentImage} alt="Recent Estimates" />
              </div>
              <div className="bento-card-content">
                <h3>Recent Estimates</h3>
                <p>Easy access to your recent work, always one click away.</p>
              </div>
            </div>

            <div className="bento-card bento-medium">
              <div className="bento-card-image">
                <img src={changeImage} alt="Explore Changes" />
              </div>
              <div className="bento-card-content">
                <h3>Explore Changes</h3>
                <p>See the impact instantly. Refresh to revert, or save to make it your new baseline.</p>
              </div>
            </div>

            <div className="bento-card bento-full">
              <div className="bento-card-image">
                <img src={intakeImage} alt="Intake Calculator" />
              </div>
              <div className="bento-card-content">
                <h3>Intake Calculator</h3>
                <p>A simple and effective intake calculation tool.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full-width Routing Section */}
      <section className="product-v2-showcase">
        <div className="product-v2-showcase-image">
          <img src={routingImage} alt="Routing" />
        </div>
        <div className="product-v2-showcase-content">
          <span className="product-v2-badge">Routing Engine</span>
          <h2>Smart Routing Options</h2>
          <p>Test routing options and immediately see the impact. Enable and disable route options and choose to optimise to minimise ECA or HRA exposure.</p>
          <ul className="product-v2-checklist">
            <li><i className="bi bi-check-circle-fill"></i> Thousands of ports</li>
            <li><i className="bi bi-check-circle-fill"></i> Tens of thousands of waypoints</li>
            <li><i className="bi bi-check-circle-fill"></i> ECA & HRA optimization</li>
          </ul>
        </div>
      </section>

      {/* Bunker Services */}
      <section className="bnk-section">
        <div className="bnk-container">
          <div className="bnk-grid">
            <div className="bnk-copy">
              <p className="about-kicker">Bunker Services</p>
              <h2 className="bnk-title">Provider prices, along your exact route.</h2>
              <p className="bnk-text">
                VoyCalc integrates with known bunker providers and brings their
                prices into the estimate, port by port, along the exact route you're sailing. Not a
                regional average, not yesterday's list.
              </p>
              <ul className="product-v2-checklist">
                <li>
                  <i className="bi bi-check-circle-fill"></i> Prices for every bunkerable port on
                  the route — with the deviation cost of getting there
                </li>
                <li>
                  <i className="bi bi-check-circle-fill"></i> Walk into bunker-trader conversations
                  prepared: you already know which ports are relevant and what they should cost
                </li>
                <li>
                  <i className="bi bi-check-circle-fill"></i> See your options — compare bunker
                  ports and route alternatives directly in the estimate
                </li>
              </ul>
            </div>
            <div className="bnk-route" aria-hidden="true">
              <div className="bnk-stop">
                <div className="bnk-stop-head">
                  <span className="bnk-port">Santos</span>
                  <span className="bnk-meta">Load</span>
                </div>
              </div>
              <div className="bnk-stop">
                <div className="bnk-stop-head">
                  <span className="bnk-port">Las Palmas</span>
                  <span className="bnk-meta">+38 nm</span>
                </div>
                <div className="bnk-chips">
                  <span className="bnk-chip">VLSFO $612</span>
                  <span className="bnk-chip">LSMGO $918</span>
                </div>
              </div>
              <div className="bnk-stop">
                <div className="bnk-stop-head">
                  <span className="bnk-port">Gibraltar</span>
                  <span className="bnk-meta">+6 nm</span>
                </div>
                <div className="bnk-chips">
                  <span className="bnk-chip bnk-chip-best">VLSFO $598 · best</span>
                  <span className="bnk-chip">LSMGO $905</span>
                </div>
              </div>
              <div className="bnk-stop">
                <div className="bnk-stop-head">
                  <span className="bnk-port">Singapore</span>
                  <span className="bnk-meta">On route</span>
                </div>
                <div className="bnk-chips">
                  <span className="bnk-chip">VLSFO $605</span>
                  <span className="bnk-chip">LSMGO $921</span>
                </div>
              </div>
              <div className="bnk-stop">
                <div className="bnk-stop-head">
                  <span className="bnk-port">Qingdao</span>
                  <span className="bnk-meta">Discharge</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distance & Areas Grid */}
      <section className="product-v2-section product-v2-section-dark">
        <div className="product-v2-container">
          <h2 className="product-v2-section-title">Distance & Compliance</h2>
          <p className="product-v2-section-subtitle">We are NOT a VMS — We simply focus on the VoyCalc!</p>

          <div className="product-v2-cards-grid">
            <div className="product-v2-card">
              <div className="product-v2-card-image">
                <img src={distanceImage} alt="Distance Table" />
              </div>
              <h3>Distance Table</h3>
              <p>Thousands of ports!</p>
            </div>

            <div className="product-v2-card">
              <div className="product-v2-card-image">
                <img src={canalImage} alt="Canals" />
              </div>
              <h3>Canals, Straits & Rivers</h3>
              <p>Common passages ready for your calcs with configurable cost features!</p>
            </div>

            <div className="product-v2-card">
              <div className="product-v2-card-image">
                <img src={hraImage} alt="HRA Areas" />
              </div>
              <h3>HRA Areas</h3>
              <p>Automatic alerts for voyages entering HRA so your risk team can handle insurance.</p>
            </div>

            <div className="product-v2-card">
              <div className="product-v2-card-image">
                <img src={ecaImage} alt="ECA Areas" />
              </div>
              <h3>ECA Areas</h3>
              <p>Possible to set a "From date" on new areas to allow for forward cargo calculations!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Configuration Tabs Section */}
      <section className="product-v2-section">
        <div className="product-v2-container">
          <h2 className="product-v2-section-title">Configurations & Flexibility</h2>
          <p className="product-v2-section-subtitle">Customize everything to match your workflow</p>

          <div className="product-v2-tabs">
            <button
              className={`product-v2-tab ${activeTab === 'vessel' ? 'active' : ''}`}
              onClick={() => setActiveTab('vessel')}
            >
              <i className="bi bi-speedometer2"></i>
              Vessel & Consumption
            </button>
            <button
              className={`product-v2-tab ${activeTab === 'routing' ? 'active' : ''}`}
              onClick={() => setActiveTab('routing')}
            >
              <i className="bi bi-signpost-split"></i>
              Routing & Costs
            </button>
            <button
              className={`product-v2-tab ${activeTab === 'emissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('emissions')}
            >
              <i className="bi bi-cloud"></i>
              Emissions & ECA
            </button>
          </div>

          <div className="product-v2-tab-content">
            {configTabs[activeTab].map((item, index) => (
              <div key={index} className="product-v2-tab-item">
                <div className="product-v2-tab-item-image">
                  <img src={item.image} alt={item.title} />
                </div>
                <div className="product-v2-tab-item-content">
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="product-v2-cta">
        <div className="product-v2-cta-content">
          <h2>Ready to streamline your voyage calculations?</h2>
          <p>We're onboarding enterprise teams — register your interest for a demo.</p>
          <RegisterInterest showSubtext={false} />
        </div>
      </section>
    </>
  );
};

export default ProductV2;
